// components
import PeriodInput from 'components/shared/PeriodInput/PeriodInput';
import { Box, Button } from '@mui/material';
import LoadingIcon from 'components/shared/LoadingIcon/LoadingIcon';

// types
import { Dialog, Select } from 'components/shared/index';
import { EntityAvailabilityDialogProps } from './EntityAvailabilityDialog.types';
import { Period } from 'components/shared/PeriodInput/PeriodInput.types';
import { EntityAvailabilityDialogContainer } from './EntityAvailabilityDialog.styles';
import { AvailabilityType, ParticipantType } from 'generated/index';

// styles
import { ActionsContainer } from '../styles';

// react/utils
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// hooks
import { mutations } from 'RestAPIs/rapini';
import { useEntityListener } from 'components/mrp/mrp.hooks';

const EntityAvailabilityDialog = ({
  entityLabel,
  planningEntityIdentifier,
  open,
  onClose,
  onOpen
}: EntityAvailabilityDialogProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  useEffect(() => {
    open && onOpen?.();
  }, [open]);

  const [period, setPeriod] = useState<Period>({
    start: dayjs().add(1, 'day').startOf('day').toDate(),
    end: dayjs().add(1, 'day').endOf('day').toDate()
  });

  const error = useMemo(() => {
    return dayjs(period.start).isBefore(period.end)
      ? null
      : t('dialogs.entityAvailability.invalid_end');
  }, [period]);

  const availabilityTypes: AvailabilityType[] = [
    'AVAILABLE',
    'NOT_AVAILABLE',
    'POSSIBLE'
  ];
  const [availabilityType, setAvailabilityType] =
    useState<AvailabilityType>('NOT_AVAILABLE');

  const { mutateAsync, isLoading } =
    mutations.useCreateAvailabilityParticipants();

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    planningEntityIdentifiers: []
  });

  const save = async () => {
    await mutateAsync({
      participantId: planningEntityIdentifier?.entityId,
      type: availabilityType,
      start: period.start.toISOString(),
      end: period.end.toISOString(),
      participantType:
        planningEntityIdentifier?.planningEntityType as ParticipantType
    });

    refetchByPlanningEntityIdentifiers([planningEntityIdentifier!]);

    onClose?.bind(null, undefined)();
  };

  return (
    <Dialog
      handleClose={onClose?.bind(null, undefined)}
      open={open}
      title={t('dialogs.entityAvailability.title')}
    >
      <EntityAvailabilityDialogContainer>
        <Box>
          {t('dialogs.entityAvailability.availabilityFor', { entityLabel })}
        </Box>
        <PeriodInput
          minDate={new Date()}
          sameDayOnly={true}
          period={period}
          clearable={false}
          onChange={setPeriod}
          error={error}
        />
        <Select
          variant="outlined"
          options={availabilityTypes.map((type) => ({
            value: type,
            label: t(`dialogs.entityAvailability.types.${type}`)
          }))}
          valueProp={availabilityType}
          onSelected={setAvailabilityType}
        />
        <ActionsContainer>
          <Button
            variant="text"
            fullWidth
            onClick={onClose?.bind(null, undefined)}
          >
            {t('dialogs.entityAvailability.cancel')}
          </Button>

          <Button disabled={isLoading} fullWidth onClick={() => save()}>
            {isLoading ? (
              <LoadingIcon size="SMALL" />
            ) : (
              t('dialogs.entityAvailability.save')
            )}
          </Button>
        </ActionsContainer>
      </EntityAvailabilityDialogContainer>
    </Dialog>
  );
};

export default EntityAvailabilityDialog;
